import {Injectable} from "@angular/core";
import {QrCopyMessageComponent} from "../qr-copy-message/qr-copy.component";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {TranslateService} from "@ngx-translate/core";
import {PaymentBackUrlParams} from "./paymentBackUrlParams";
import {SidebarComponent} from "../sidebar/sidebar.component";
import {MySubscriptionsComponent} from "../my-subscriptions/my-subscriptions.component";
import {MockupDataModel} from "../qr-copy-message/mockup-data.model";
import {TransactionOpaque} from "../models/payment/opaque/transactionOpaque";
import {SubscriptionBuyingOpaque} from "../models/payment/opaque/subscriptionBuyingOpaque";
import {AdditionalSubscriptionAddingOpaque} from "../models/payment/opaque/additionalSubscriptionAddingOpaque";
import {AdditionalSubscriptionBuyingOpaque} from "../models/payment/opaque/additionalSubscriptionBuyingOpaque";
import {ProcessPaymentDetailsRequest} from "../models/payment/processPaymentDetailsRequest";
import {ProcessPaymentDetailsResponse} from "../models/payment/processPaymentDetailsResponse";
import {Subject} from "rxjs";
import {WalletToWalletPaymentReceipt} from "../models/payment/walletToWalletPaymentReceipt";
import {PaymentReceiptComponent} from "../payment-receipt/payment-receipt.component";
import {SubscriptionBuyingService} from "../service/subscription-buying.service";
import {AmeriaWalletToWalletService} from "../service/ameria-wallet-to-wallet.service";
import {SnackBarUtil} from "../qr-copy-message/snack-bar-util.service";
import {SUCCESS_PAYMENT_TRANSACTION_MESSAGE} from "./subscription-payment.constant";

@Injectable({
  providedIn: "root"
})
export class PaymentBackUrlParamsHandlerService {

  public static paymentSuccess: Subject<void> = new Subject<void>();

  constructor(
    private subscriptionBuyingService: SubscriptionBuyingService,
    private ameriaWalletToWalletService: AmeriaWalletToWalletService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {
  }

  public handleQueryParams(): void {
    this.route.queryParams.subscribe((params: Params) => {
      const paymentBackUrlParams: PaymentBackUrlParams = new PaymentBackUrlParams(params['paymentID'], Number(params['orderID']), params['opaque']);

      if (paymentBackUrlParams.paymentID && paymentBackUrlParams.orderID) {
        this.processPaymentDetails(paymentBackUrlParams);
      }
    });
  }

  private processPaymentDetails(paymentBackUrlParams: PaymentBackUrlParams): void {
    this.removeQueryParamsFromUrl();

    const {paymentID, orderID} = paymentBackUrlParams;
    const opaque: TransactionOpaque = this.getOpaqueFromParams(paymentBackUrlParams);
    const processPaymentDetailsRequest: ProcessPaymentDetailsRequest = new ProcessPaymentDetailsRequest(paymentID!, orderID!, opaque);

    if(TransactionOpaque.isNoArgs(opaque) || !paymentBackUrlParams.opaque) {
      this.processWalletToWalletRequest(processPaymentDetailsRequest);
      return;
    }

    this.processSubscriptionOpaque(processPaymentDetailsRequest);
  }

  private processWalletToWalletRequest(processPaymentDetailsRequest: ProcessPaymentDetailsRequest) {
    this.ameriaWalletToWalletService.processAfterInit(processPaymentDetailsRequest).subscribe({
      next: (response: WalletToWalletPaymentReceipt) => {
        PaymentReceiptComponent.paymentReceipt = response;
        PaymentReceiptComponent.loading = false;
      },
      error: () => {
        this.showErrorSnackbar();
      }
    });
  }

  private processSubscriptionOpaque(processPaymentDetailsRequest: ProcessPaymentDetailsRequest) {
    this.subscriptionBuyingService.processAfterInit(processPaymentDetailsRequest).subscribe({
      next: (response: ProcessPaymentDetailsResponse) => {
        SidebarComponent.initializer.next(true);
        PaymentReceiptComponent.loading = false;
        const subscriptionResponse = response?.subscriptionResponse;
        if (subscriptionResponse) {
          MySubscriptionsComponent.isAdditionalCardsBought.next(subscriptionResponse);
        }
        const data = MockupDataModel.success(this.translateService.instant(SUCCESS_PAYMENT_TRANSACTION_MESSAGE));
        this.showSnackbar(data, SnackBarUtil.CLASSES.success);
        PaymentBackUrlParamsHandlerService.paymentSuccess.next();
      },
      error: () => {
        this.showErrorSnackbar();
      }
    });
  }

  private getOpaqueFromParams(paymentBackUrlParams: PaymentBackUrlParams): TransactionOpaque {
    const opaque = paymentBackUrlParams.opaque;
    if (!opaque) {
      return TransactionOpaque.noArgs();
    }

    const opaqueObjFromParams = JSON.parse(opaque);
    const {subscriptionInfoId, isMonthly, isAddAdditional, additionalCardsCount, promoCode} = opaqueObjFromParams;

    if (subscriptionInfoId) {
      const subscriptionBuyingOpaque = new SubscriptionBuyingOpaque(subscriptionInfoId, isMonthly, promoCode);
      return TransactionOpaque.withSubscriptionBuyingOpaque(subscriptionBuyingOpaque);
    }

    if (isAddAdditional) {
      const additionalSubscriptionAddingOpaque = new AdditionalSubscriptionAddingOpaque(additionalCardsCount);
      return TransactionOpaque.withAdditionalSubscriptionAddingOpaque(additionalSubscriptionAddingOpaque);
    }

    if (additionalCardsCount) {
      const additionalSubscriptionBuyingOpaque = new AdditionalSubscriptionBuyingOpaque(additionalCardsCount, isMonthly);
      return TransactionOpaque.withAdditionalSubscriptionBuyingOpaque(additionalSubscriptionBuyingOpaque);
    }

    return TransactionOpaque.noArgs();
  }

  private removeQueryParamsFromUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true
    }).catch();
  }

  private showErrorSnackbar() {
    const message = this.translateService.instant('failed_payment_response');
    const data = MockupDataModel.error(message);
    this.showSnackbar(data, SnackBarUtil.CLASSES.error);
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openFromComponent(QrCopyMessageComponent, {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data,
    });
  }
}
